import { DetailSkeleton } from 'components/skeletons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { Form } from './form';
import { useDeleteAgent, useGetAgent, useUpdateAgent } from 'core/agents/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { CardHeader } from 'components/cards/header';
import { AxiosError } from 'axios';
import { roleList } from './constants';
import { Delete } from '@mui/icons-material';

type Props = {
    agentData: any;
};

const EditForm = ({agentData}: Props) => {
    const navigate = useNavigate();

    // States
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    // Callbacks
    const handleClickOpen = () => setOpenConfirmDialog(true);
    const handleClose = () => setOpenConfirmDialog(false);

    // Form
    const methods = useForm({mode: 'onChange', defaultValues: useMemo(() => agentData, [agentData])});
    const { formState: { errors, dirtyFields }, reset, setError } = methods;
    const onFormSubmit = (data: any) => mutation.mutate({id: agentData.id, data: data});
    const onReset = useCallback(() => reset(agentData), [agentData, reset]);
    useEffect(() => reset(agentData), [agentData, reset]);

    // Mutation
    const mutation = useUpdateAgent({
        onError: (error) => {
            toast.error("Une Erreur est survenue");

            if (error instanceof AxiosError) {
                const {full_messages, ...errors} = error.response?.data?.errors ?? {};
                Object.entries<Array<string>>(errors).forEach(([key, value]) => {
                    if (!['base'].includes(key)) { setError(key, {type: 'custom', message: value.join(', ')}); }
                });
            }
        },
        onSuccess: (data: any) => {
            toast.success("Le collaborateur a été modifié");
            navigate(`/dashboard/agents/${data.id}`);
        }
    });
    const {mutate: mutateDelete, isLoading: isDeleteLoading} = useDeleteAgent({
        onSuccess: (data: any) => {
            toast.success('Le collaborateur a bien été supprimé.');
            navigate(`/dashboard/agents/`);
        },
        onError: (error: any) => toast.error('Le collaborateur n\'a pas pu être supprimé.'),
    });

    return (
        <Box component='main'>
            <FormProvider {...methods}>
                <Box component='form' onSubmit={methods.handleSubmit(onFormSubmit)} noValidate>
                    <CardHeader
                        title={`${agentData.first_name} ${agentData.last_name}`}
                        avatarSubstitute={agentData.first_name[0].toUpperCase()}
                        chips={[
                            {color: agentData.role === 'admin' ? 'error' : agentData.role === 'director' ? 'warning' : agentData.role === 'manager' ? 'success' : 'info', label: roleList[agentData.role] }
                        ]}
                        rightComponent={(
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 3, gap: '1rem' }}>
                                <Button variant='contained' color='secondary' onClick={onReset} disabled={mutation.isLoading || Object.keys(dirtyFields).length === 0}>
                                    Annuler
                                </Button>
                                <Tooltip title={Object.keys(errors).length > 0 ? 'Veuillez fixer les erreurs' : (Object.keys(dirtyFields).length === 0 ? 'Aucun champs a été modifié' : "")}>
                                    <span>
                                        <Button type='submit' variant='contained' color='primary' sx={{ color: 'white' }} disabled={mutation.isLoading || Object.keys(errors).length > 0 || Object.keys(dirtyFields).length === 0}>
                                            {mutation.isLoading && <CircularProgress size={10} />}
                                            Sauvegarder
                                        </Button>
                                    </span>
                                </Tooltip>



                                <Button variant='outlined' size='small' color='warning' onClick={handleClickOpen} disabled={isDeleteLoading}>
                                    <Delete />
                                </Button>
                                <Dialog open={openConfirmDialog} onClose={handleClose}>
                                    <DialogTitle>Supprimer?</DialogTitle>
                                    <DialogContent>
                                        <DialogContentText>
                                            Êtes-vous sûr(e) de vouloir supprimer ce collaborateur?
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleClose}>Annuler</Button>
                                        <Button
                                            onClick={() => {
                                                mutateDelete(agentData.id);
                                                handleClose();
                                            }}
                                            autoFocus
                                        >Supprimer</Button>
                                    </DialogActions>
                                </Dialog>
                            </Box>
                        )}
                    />
                    <Form />
                </Box>
            </FormProvider>
        </Box>
    );
};

const EditPage = () => {
    const {agentId} = useParams();
    const {data: agentData, isLoading} = useGetAgent(Number(agentId!), {onError: (err) => toast.error("Une Erreur est survenue")});

    return (
        <Box>
            {agentData ? (<EditForm agentData={agentData} />) : isLoading ? (<DetailSkeleton />) : (<Typography>Une erreur est survenue</Typography>)}
        </Box>
    );
};

export { EditPage };